<template>
  <div class="rehab-person-page" :style="rehabMerchantThemeStyle">
    <!-- 头部区域 -->
    <div class="rehab-person__header">
      <div class="rehab-person__header-bg"></div>
      <div class="rehab-person__header-content">
        <h1 class="rehab-person__title">
          <img v-if="rehabMerchantConfig.logo" :src="rehabMerchantConfig.logo" class="rehab-person__title-logo" alt="merchant logo">
          <van-icon v-else name="user-circle-o"/>
          {{ personTitle }}
        </h1>
        <div class="rehab-person__subtitle">{{ personSubtitle }}</div>
      </div>
    </div>

    <div class="rehab-person">
      <!-- 用户信息卡片 -->
      <div class="rehab-person__card" @click="onUserAreaClick">
        <div class="rehab-person__card-bg"></div>
        <div class="rehab-person__card-content">
          <div class="rehab-person__avatar">
            <van-icon color="#07c160" name="contact" size="28"/>
            <div v-if="checkLogin()" class="rehab-person__avatar-badge"></div>
          </div>
          <div class="rehab-person__info">
            <div class="rehab-person__greeting">
              <van-icon color="#07c160" name="good-job-o" size="16"/>
              亲~ {{ getGreeting() }}好
            </div>
            <div v-if="name" class="rehab-person__name">{{ name }}</div>
            <div v-else class="rehab-person__login-tip">
              <van-icon name="arrow"/>
              点击登录开启康复之旅
            </div>
          </div>
          <div v-if="checkLogin()" class="rehab-person__settings" @click.stop="showSettingsPopup = true">
            <van-icon color="#64748b" name="setting-o" size="20"/>
          </div>
        </div>
      </div>

      <!-- VIP会员与康复指导权益融合卡片 -->
      <div v-if="checkLogin()" :class="{ 'vip-active': isVip }" class="rehab-person__vip-guidance-card">
        <div class="rehab-person__vip-guidance-bg"></div>
        <div class="rehab-person__vip-guidance-content">
          <!-- VIP状态区域 -->
          <div class="rehab-person__vip-section">
            <div class="rehab-person__vip-info">
              <div class="rehab-person__vip-icon">
                <van-icon :color="isVip ? '#ff6b35' : '#999'" name="diamond" size="24"/>
                <div v-if="isVip" class="rehab-person__vip-shine"></div>
              </div>
              <div class="rehab-person__vip-text">
                <div class="rehab-person__vip-title">
                  <span v-if="isVipLoading">
                    <van-loading color="#ff6b35" size="14"/>
                    检查中...
                  </span>
                  <span v-else-if="isVip">
                    VIP 会员
                  </span>
                  <span v-else>
                    普通用户
                  </span>
                </div>
                <div class="rehab-person__vip-desc">
                  <span v-if="isVipLoading">正在检查会员状态</span>
                  <span v-else-if="isVip && vipInfo">有效期至 {{ formatVipExpireDate(vipInfo.endTime) }}</span>
                  <span v-else>开通会员享受更多权益</span>
                </div>
              </div>
            </div>
            <div class="rehab-person__vip-action">
              <van-button
                v-if="!isVip"
                color="linear-gradient(135deg, #ff6b35, #ff8f3f)"
                round
                size="small"
                @click="goToVipUpgrade"
              >
                <van-icon name="diamond"/>
                开通会员
              </van-button>
              <van-button
                v-else
                color="linear-gradient(135deg, #07c160, #36d174)"
                round
                size="small"
                @click="goToVipUpgrade"
              >
                <van-icon name="upgrade"/>
                续费
              </van-button>
            </div>
          </div>

          <van-button
            plain
            round
            size="large"
            block
            class="rehab-person__vip-redeem-btn"
            @click="openVipRedeemPopup"
          >
            <van-icon name="coupon-o"/>
            兑换 VIP
          </van-button>

          <!-- 虚线分割 -->
          <div class="rehab-person__dashed-divider"></div>

          <!-- 康复指导权益区域 -->
          <div class="rehab-person__guidance-section">
            <div class="rehab-person__guidance-header">
              <div class="rehab-person__guidance-title">专业康复指导</div>
              <div class="rehab-person__guidance-desc">
                {{ isVip ? '尊享VIP专属指导服务' : '升级VIP享受更多指导次数' }}
              </div>
            </div>
            <div class="rehab-person__guidance-stats">
              <div class="rehab-person__guidance-stats-item">
                <div class="rehab-person__guidance-stats-label">剩余次数</div>
                <div class="rehab-person__guidance-stats-value">
                  <span v-if="isGuidanceLoading" class="loading-text">
                    <van-loading size="12"/>
                  </span>
                  <span v-else class="number">{{ guidanceStats.remaining }}</span>
                  <span class="unit">次</span>
                </div>
              </div>
              <div class="rehab-person__guidance-stats-item">
                <div class="rehab-person__guidance-stats-label">已使用</div>
                <div class="rehab-person__guidance-stats-value">
                  <span v-if="isGuidanceLoading" class="loading-text">
                    <van-loading size="12"/>
                  </span>
                  <span v-else class="number">{{ guidanceStats.used }}</span>
                  <span class="unit">次</span>
                </div>
              </div>
              <div class="rehab-person__guidance-stats-item">
                <div class="rehab-person__guidance-stats-label">总配额</div>
                <div class="rehab-person__guidance-stats-value">
                  <span v-if="isGuidanceLoading" class="loading-text">
                    <van-loading size="12"/>
                  </span>
                  <span v-else class="number">{{ guidanceStats.total }}</span>
                  <span class="unit">次</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 学习时长统计 -->
      <div v-if="checkLogin()" class="rehab-person__study-section">
        <!-- <div class="rehab-person__section-title">
          <van-icon name="chart-trending-o" size="18" color="#07c160" />
          <span>学习统计</span>
          <div class="rehab-person__section-line"></div>
        </div> -->
        <div class="rehab-person__study-stats">
          <div class="rehab-person__study-stats-item today">
            <div class="rehab-person__study-icon">
              <van-icon color="#ff6b35" name="clock-o" size="18"/>
            </div>
            <div class="rehab-person__study-content">
              <div class="rehab-person__study-stats-label">今日学习</div>
              <div class="rehab-person__study-stats-value">
                <span v-if="isStudyLoading" class="loading-text">
                  <van-loading size="12"/>
                </span>
                <span v-else class="rehab-person__flip">
                  <transition name="flip">
                    <span :key="studyMinutes.today" class="number">{{ studyMinutes.today }}</span>
                  </transition>
                  <span class="unit">分钟</span>
                </span>
              </div>
            </div>
          </div>

          <div class="rehab-person__study-stats-item week">
            <div class="rehab-person__study-icon">
              <van-icon color="#07c160" name="calendar-o" size="18"/>
            </div>
            <div class="rehab-person__study-content">
              <div class="rehab-person__study-stats-label">本周学习</div>
              <div class="rehab-person__study-stats-value">
                <span v-if="isStudyLoading" class="loading-text">
                  <van-loading size="12"/>
                </span>
                <span v-else class="rehab-person__flip">
                  <transition name="flip">
                    <span :key="studyMinutes.week" class="number">{{ studyMinutes.week }}</span>
                  </transition>
                  <span class="unit">分钟</span>
                </span>
              </div>
            </div>
          </div>

          <div class="rehab-person__study-stats-item total">
            <div class="rehab-person__study-icon">
              <van-icon color="#6366f1" name="fire-o" size="18"/>
            </div>
            <div class="rehab-person__study-content">
              <div class="rehab-person__study-stats-label">累计学习</div>
              <div class="rehab-person__study-stats-value">
                <span v-if="isStudyLoading" class="loading-text">
                  <van-loading size="12"/>
                </span>
                <span v-else class="rehab-person__flip">
                  <transition name="flip">
                    <span :key="studyMinutes.total" class="number">{{ studyMinutes.total }}</span>
                  </transition>
                  <span class="unit">分钟</span>
                </span>
              </div>
            </div>
          </div>

          <div class="rehab-person__study-stats-item courses">
            <div class="rehab-person__study-icon">
              <van-icon color="#f59e0b" name="bookmark-o" size="18"/>
            </div>
            <div class="rehab-person__study-content">
              <div class="rehab-person__study-stats-label">已观看课程</div>
              <div class="rehab-person__study-stats-value">
                <span v-if="isStudyLoading" class="loading-text">
                  <van-loading size="12"/>
                </span>
                <span v-else class="rehab-person__flip">
                  <transition name="flip">
                    <span :key="studyMinutes.courses" class="number">{{ studyMinutes.courses }}</span>
                  </transition>
                  <span class="unit">门</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- VIP购买弹窗 -->
    <vip-purchase-popup
      :show="showVipPopup"
      :merchant="rehabMerchantState.queryMerchant || rehabMerchantConfig.key"
      @close="showVipPopup = false"
      @purchase="onVipPurchase"
    />

    <vip-redeem-popup
      :show="showRedeemPopup"
      :loading="isRedeemingVip"
      :merchant-label="rehabMerchantConfig.name"
      @close="showRedeemPopup = false"
      @submit="submitVipRedeem"
    />

    <!-- 设置弹窗 -->
    <van-popup v-model="showSettingsPopup" position="bottom" round>
      <div class="settings-popup">
        <div class="settings-popup__header">
          <h3 class="settings-popup__title">设置</h3>
          <van-icon
            class="settings-popup__close"
            color="#999"
            name="cross"
            size="18"
            @click="showSettingsPopup = false"
          />
        </div>
        <div class="settings-popup__content">
          <div class="settings-popup__item" @click="confirmLogout">
            <div class="settings-popup__item-icon">
              <van-icon color="#ff4757" name="sign" size="20"/>
            </div>
            <div class="settings-popup__item-text">
              <div class="settings-popup__item-title">退出账号</div>
              <div class="settings-popup__item-desc">退出当前登录账号</div>
            </div>
            <van-icon color="#ccc" name="arrow" size="16"/>
          </div>
        </div>
      </div>
    </van-popup>
  </div>
</template>
<script>
import BaseTitleBarScrollPage from '@/h5/doucan/components/BaseTitleBarScrollPage.vue'
import VipPurchasePopup from '@/h5/doucan/components/VipPurchasePopup.vue'
import VipRedeemPopup from '@/h5/doucan/components/VipRedeemPopup.vue'
import TicaoDao from '@/h5/doucan/network/Ticao.js'
import VipDao from '@/h5/doucan/network/VipDao.js'
import rehabMerchantMixin from './rehabMerchantMixin'
import { filterRehabCoursesByMerchant } from './merchantConfig'
import { listMerchantCourseHistory } from './merchantCourseSource'

export default {
  name: 'RehabCoursesPerson',
  mixins: [rehabMerchantMixin],
  components: {
    BaseTitleBarScrollPage,
    VipPurchasePopup,
    VipRedeemPopup
  },
  data () {
    return {
      isApp: false,
      name: '',
      studyMinutes: {
        today: 0,
        week: 0,
        total: 0,
        courses: 0
      },
      isStudyLoading: false,
      vipInfo: null,
      isVipLoading: false,
      showVipPopup: false, // VIP购买弹窗显示状态
      showRedeemPopup: false,
      isRedeemingVip: false,
      // 康复指导权益数据
      guidanceStats: {
        remaining: 0,
        used: 0,
        total: 0
      },
      isGuidanceLoading: false,
      // 设置弹窗显示状态
      showSettingsPopup: false
    }
  },
  activated () {
    // 组件被 keep-alive 激活时刷新一次
    if (this.checkLogin()) {
      this.fetchPlaybackStudyMinutes().catch(() => {
      })
      this.fetchVipInfo().catch(() => {
      })
      this.fetchGuidanceStats().catch(() => {
      })
    }
  },
  mounted () {
    const params = new URLSearchParams(window.location.search)
    this.isApp = params.get('isApp') === '1' || params.get('isapp') === '1'
    this.initRehabMerchantFromRoute()
    this.applyRehabMerchantTheme()

    // 获取用户信息
    if (this.$store.state.user.profile.isLogin) {
      this.name =
        this.$store.state.user.profile.name +
        '（' +
        this.$store.state.user.profile.phone.slice(7, 11) +
        '）'

      // 静默拉取播放记录并统计学习时长（单位：分钟）
      this.isStudyLoading = true
      this.fetchPlaybackStudyMinutes().catch((e) => {
        console.warn('读取播放记录失败:', e)
        this.isStudyLoading = false
      })

      // 拉取 VIP 信息
      this.fetchVipInfo().catch((e) => {
        console.warn('读取VIP信息失败:', e)
      })

      // 拉取康复指导权益信息
      this.fetchGuidanceStats().catch((e) => {
        console.warn('读取康复指导权益失败:', e)
      })
    }

    // 页面可见变化时刷新一次
    this._onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && this.checkLogin()) {
        this.fetchPlaybackStudyMinutes().catch(() => {
        })
        this.fetchVipInfo().catch(() => {
        })
        this.fetchGuidanceStats().catch(() => {
        })
      }
    }
    document.addEventListener('visibilitychange', this._onVisibilityChange)

    this.hideLoading()
  },
  beforeDestroy () {
    // 清理可见性监听
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange)
      this._onVisibilityChange = null
    }
  },
  methods: {
    hideLoading () {
      this.$refs.container && this.$refs.container.stopLoading && this.$refs.container.stopLoading()
    },
    getGreeting () {
      const currentHour = new Date().getHours()
      if (currentHour < 12) {
        return '早上'
      } else if (currentHour < 13) {
        return '中午'
      } else if (currentHour < 18) {
        return '下午'
      } else {
        return '晚上'
      }
    },
    onUserAreaClick () {
      if (!this.checkLogin()) {
        const query = this.rehabMerchantState.queryMerchant ? `&merchant=${encodeURIComponent(this.rehabMerchantState.queryMerchant)}` : ''
        this.$router.push(`/login/index?returnTo=rehab-courses${query}`)
      }
    },
    async fetchPlaybackStudyMinutes () {
      // 未登录不统计
      if (!this.checkLogin()) return

      this.isStudyLoading = true
      const userId = this.$store.state.user.profile.userId
      const records = this.useMarkdownMerchantCourses
        ? listMerchantCourseHistory(this.rehabMerchantConfig)
        : await TicaoDao.listAppletMyCourse(userId)
      const merchantRecords = filterRehabCoursesByMerchant(records, this.rehabMerchantConfig)
      if (!merchantRecords || !Array.isArray(merchantRecords)) {
        this.studyMinutes = {
          today: 0,
          week: 0,
          total: 0
        }
        this.isStudyLoading = false
        return
      }

      // 归一化并汇总（单位：秒）
      let todaySeconds = 0
      let weekSeconds = 0
      let totalSeconds = 0
      // 统计不重复的课程
      const uniqueCourses = new Set()

      const now = new Date()
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
      // 将周一作为一周开始
      const day = now.getDay() === 0 ? 7 : now.getDay()
      const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - (day - 1) * 24 * 60 * 60 * 1000

      const toNumber = (v, def = 0) => {
        const n = Number(v)
        return Number.isFinite(n) ? n : def
      }

      merchantRecords.forEach((item) => {
        // 常见字段兼容：总时长秒（兼容 onlineDuration）
        console.log('历史记录item', item)

        const totalPlayed = toNumber(
          item.totalPlayedSec || item.totalSeconds || item.playedSeconds || item.lastWatchDuration || item.durationSec || item.duration,
          0
        )
        totalSeconds += Math.max(0, totalPlayed)

        // 今日/本周可能由后端直接返回聚合字段
        const todayPlayed = toNumber(item.todaySeconds || item.playedSecondsToday || item.todayPlayedSec, -1)
        const weekPlayed = toNumber(item.weekSeconds || item.playedSecondsThisWeek || item.weekPlayedSec, -1)
        if (todayPlayed >= 0) todaySeconds += todayPlayed
        if (weekPlayed >= 0) weekSeconds += weekPlayed

        // 如果没有聚合字段，优先用 lastWatchTime + lastWatchDuration 统计；再回退到会话明细
        if (todayPlayed < 0 || weekPlayed < 0) {
          const lastWatchTs = toNumber(item.lastWatchTime || item.lastWatchAt || item.updatedAt || item.lastAt, 0)
          const lastWatchDur = toNumber(item.lastWatchDuration || item.lastDuration, -1)
          if (lastWatchTs > 0 && lastWatchDur >= 0) {
            if (lastWatchTs >= startOfToday) todaySeconds += lastWatchDur
            if (lastWatchTs >= startOfWeek) weekSeconds += lastWatchDur
          } else if (Array.isArray(item.sessions)) {
            item.sessions.forEach((s) => {
              const sessionSec = toNumber(s.seconds || s.durationSec || s.duration, 0)
              const ts = toNumber(s.timestamp || s.updatedAt || s.time || s.startTime, 0)
              if (ts >= startOfToday) todaySeconds += sessionSec
              if (ts >= startOfWeek) weekSeconds += sessionSec
            })
          }
        }

        // 统计不重复的课程
        if (item.courseId || item.id) {
          uniqueCourses.add(item.courseId || item.id)
        }
      })

      // 转换为分钟（向下取整）
      const toMinutes = (sec) => Math.max(0, Math.floor(toNumber(sec, 0) / 60))
      this.studyMinutes = {
        today: toMinutes(todaySeconds),
        week: toMinutes(weekSeconds),
        total: toMinutes(totalSeconds),
        courses: uniqueCourses.size
      }
      this.isStudyLoading = false

      // 便于调试
      console.log('学习分钟统计:', this.studyMinutes)
    },
    checkLogin () {
      return this.$store.state.user.profile.isLogin
    },
    logout () {
      this.$store.commit('user/setOpenId', '')
      this.$store.commit('user/logout')
      this.name = ''
      this.vipInfo = null
      this.guidanceStats = {
        remaining: 0,
        used: 0,
        total: 0
      }
      this.$toast('退出成功')
      setTimeout(() => {
        location.reload()
      }, 500)
    },
    async fetchVipInfo () {
      if (!this.checkLogin()) return

      this.isVipLoading = true
      try {
        const userId = this.$store.state.user.profile.userId
        if (!userId) {
          this.vipInfo = null
          return
        }

        // 使用新的VipDao方法获取详细VIP信息（包含康复指导次数）
        const vipInfo = await VipDao.getUserVipInfo(userId)

        if (vipInfo) {
          this.vipInfo = vipInfo
          console.log('VIP信息（含康复指导次数）:', {
            totalServiceCount: vipInfo.totalServiceCount,
            usedServiceCount: vipInfo.usedServiceCount,
            remainingServiceCount: vipInfo.remainingServiceCount
          })
        } else {
          this.vipInfo = null
        }
      } catch (error) {
        console.error('获取VIP信息失败:', error)
        this.vipInfo = null
      } finally {
        this.isVipLoading = false
      }
    },
    formatVipExpireDate (endTime) {
      if (!endTime) return ''
      try {
        const date = new Date(endTime)
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        return `${year}-${month}-${day}`
      } catch (error) {
        return ''
      }
    },
    goToVipUpgrade () {
      // 显示VIP购买弹窗
      this.showVipPopup = true
    },
    openVipRedeemPopup () {
      this.showRedeemPopup = true
    },
    async refreshVipSection () {
      await Promise.all([
        this.fetchVipInfo(),
        this.fetchGuidanceStats()
      ])
    },
    async submitVipRedeem (exchangeCode) {
      if (!this.checkLogin()) {
        this.$toast('请先登录')
        this.showRedeemPopup = false
        return
      }

      const merchantCode = this.rehabMerchantState.queryMerchant || this.rehabMerchantConfig.key
      const profile = this.$store.state.user.profile || {}
      this.isRedeemingVip = true
      try {
        const redeemResult = await VipDao.redeemVipCode({
          merchantCode,
          exchangeCode,
          userId: profile.userId,
          phone: profile.phone
        })
        this.showRedeemPopup = false
        await this.refreshVipSection()
        const endTimeText = this.formatVipExpireDate(redeemResult.vipEndTime)
        this.$toast.success(endTimeText ? `兑换成功，有效期至 ${endTimeText}` : '兑换成功')
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || '兑换失败，请稍后再试'
        this.$toast.fail(message)
      } finally {
        this.isRedeemingVip = false
      }
    },
    // VIP购买处理
    onVipPurchase (plan) {
      console.log('用户选择购买VIP套餐:', plan)
      // 这里可以跳转到支付页面或调用支付接口
      this.$toast(`对接中....正在跳转到${plan.name}购买页面...`)
      // TODO: 实现具体的购买逻辑
      // this.$router.push(`/vip/purchase?planId=${plan.id}`)
    },

    // 确认退出登录
    confirmLogout () {
      this.showSettingsPopup = false
      this.$dialog.confirm({
        title: '确认退出',
        message: '确定要退出当前账号吗？',
        confirmButtonText: '退出',
        cancelButtonText: '取消',
        confirmButtonColor: '#ff4757'
      }).then(() => {
        this.logout()
      }).catch(() => {
        // 用户取消退出
      })
    },

    // 获取康复指导权益统计
    async fetchGuidanceStats () {
      if (!this.checkLogin()) return

      this.isGuidanceLoading = true
      try {
        const userId = this.$store.state.user.profile.userId
        if (!userId) {
          this.guidanceStats = {
            remaining: 0,
            used: 0,
            total: 0
          }
          return
        }

        // 使用VipDao获取康复指导服务统计
        const guidanceData = await VipDao.getServiceStats(userId)

        this.guidanceStats = {
          total: guidanceData.total || 0,
          used: guidanceData.used || 0,
          remaining: guidanceData.remaining || 0
        }

        // 实际接口调用示例：
        // const { data: resp } = await this.$http.get('/guidance/stats', {
        //   params: { userId }
        // })
        // if (resp && resp.code === 200 && resp.data) {
        //   this.guidanceStats = resp.data
        // }
      } catch (error) {
        console.error('获取康复指导权益失败:', error)
        this.guidanceStats = {
          remaining: 0,
          used: 0,
          total: 0
        }
      } finally {
        this.isGuidanceLoading = false
      }
    }
  },
  computed: {
    personTitle () {
      return `${this.rehabMerchantConfig.shortName || this.rehabMerchantConfig.name}个人中心`
    },
    personSubtitle () {
      return `欢迎来到${this.rehabMerchantConfig.name}康复学习空间`
    },
    isVip () {
      return this.vipInfo !== null
    }
  }
}
</script>
<style scoped>
/* 页面整体样式 */
.rehab-person__title-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.16);
  padding: 3px;
}

.rehab-person-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

/* 头部区域 */
.rehab-person__header {
  position: relative;
  padding: 20px 16px 30px;
  overflow: hidden;
}

.rehab-person__header-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--rehab-theme-color, #07c160) 0%, var(--rehab-theme-color-dark, #36d174) 50%, var(--rehab-accent-color, #4ade80) 100%);
  opacity: 0.95;
}

.rehab-person__header-bg::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E") repeat;
  animation: float 20s infinite linear;
}

.rehab-person__header-content {
  position: relative;
  z-index: 2;
  text-align: center;
}

.rehab-person__title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.rehab-person__subtitle {
  margin-top: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

/* 内容区域 */
.rehab-person {
  padding: 0 16px 20px;
  margin-top: -15px;
  position: relative;
  z-index: 1;
}

/* 用户信息卡片 */
.rehab-person__card {
  position: relative;
  cursor: pointer;
  margin-bottom: 16px;
  border-radius: 16px;
  overflow: hidden;
  transform: translateY(0);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.rehab-person__card:active {
  transform: translateY(2px);
}

.rehab-person__card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.03);
}

.rehab-person__card-content {
  position: relative;
  display: flex;
  align-items: center;
  padding: 20px;
  z-index: 2;
}

.rehab-person__avatar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #e8f5e8 0%, #f0f9ff 100%);
  border-radius: 50%;
  margin-right: 16px;
  box-shadow: 0 4px 12px rgba(7, 193, 96, 0.15);
}

.rehab-person__avatar-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, #07c160, #36d174);
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.rehab-person__info {
  flex: 1;
}

.rehab-person__greeting {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.rehab-person__name {
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
}

.rehab-person__login-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  color: #07c160;
  font-weight: 500;
}

.rehab-person__settings {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(100, 116, 139, 0.1);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-left: auto;
}

.rehab-person__settings:hover {
  background: rgba(100, 116, 139, 0.2);
  transform: scale(1.05);
}

.rehab-person__settings:active {
  transform: scale(0.95);
}

/* VIP会员与康复指导权益融合卡片 */
.rehab-person__vip-guidance-card {
  position: relative;
  border-radius: 16px;
  margin-bottom: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.rehab-person__vip-guidance-card.vip-active {
  transform: scale(1.01);
}

.rehab-person__vip-guidance-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #fff7ed 0%, #fef3c7 50%, #fde68a 100%);
  box-shadow: 0 8px 20px rgba(251, 146, 60, 0.15), 0 3px 8px rgba(251, 146, 60, 0.1);
}

.rehab-person__vip-guidance-card.vip-active .rehab-person__vip-guidance-bg {
  background: linear-gradient(135deg, #ff6b35 0%, #ff8f3f 50%, #ffa726 100%);
  box-shadow: 0 12px 30px rgba(255, 107, 53, 0.3), 0 4px 12px rgba(255, 107, 53, 0.15);
}

.rehab-person__vip-guidance-content {
  position: relative;
  padding: 20px;
  z-index: 2;
}

/* VIP状态区域 */
.rehab-person__vip-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.rehab-person__vip-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.rehab-person__vip-icon {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  margin-right: 16px;
  box-shadow: 0 4px 12px rgba(255, 143, 63, 0.2);
}

.rehab-person__vip-shine {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  background: radial-gradient(circle, #ffd700 0%, #ffb700 100%);
  border-radius: 50%;
  animation: shine 2s infinite;
}

.rehab-person__vip-text {
  flex: 1;
}

.rehab-person__vip-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 17px;
  font-weight: 700;
  color: #ea580c;
  margin-bottom: 4px;
}

.rehab-person__vip-guidance-card.vip-active .rehab-person__vip-title {
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.rehab-person__vip-desc {
  font-size: 13px;
  color: #9a3412;
  line-height: 1.4;
  font-weight: 500;
}

.rehab-person__vip-guidance-card.vip-active .rehab-person__vip-desc {
  color: rgba(255, 255, 255, 0.9);
}

.rehab-person__vip-action {
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;
}

.rehab-person__vip-redeem-btn {
  margin-bottom: 16px;
  height: 44px;
  border-color: rgba(154, 52, 18, 0.22);
  color: #9a3412;
  background: rgba(255, 255, 255, 0.82);
  font-size: 15px;
  font-weight: 600;
}

/* 虚线分割 */
.rehab-person__dashed-divider {
  height: 2px;
  background: repeating-linear-gradient(
    to right,
    rgba(255, 255, 255, 0.6) 0px,
    rgba(255, 255, 255, 0.6) 8px,
    transparent 8px,
    transparent 16px
  );
  margin: 16px 0;
  position: relative;
}

/* 康复指导权益区域 */
.rehab-person__guidance-section {
  margin-top: 0;
}

.rehab-person__guidance-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.rehab-person__guidance-title {
  font-size: 16px;
  font-weight: 600;
  color: #ea580c;
  flex-shrink: 0;
}

.rehab-person__vip-guidance-card.vip-active .rehab-person__guidance-title {
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.rehab-person__guidance-desc {
  font-size: 12px;
  color: #9a3412;
  line-height: 1.4;
  font-weight: 500;
  text-align: right;
  flex: 1;
  margin-left: 16px;
}

.rehab-person__vip-guidance-card.vip-active .rehab-person__guidance-desc {
  color: rgba(255, 255, 255, 0.85);
}

.rehab-person__guidance-stats {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.rehab-person__guidance-stats-item {
  flex: 1;
  text-align: center;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 12px 8px;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.rehab-person__guidance-stats-label {
  font-size: 12px;
  color: #9a3412;
  margin-bottom: 6px;
  font-weight: 500;
}

.rehab-person__guidance-stats-value {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
}

.rehab-person__guidance-stats-value .number {
  font-size: 18px;
  font-weight: 700;
  color: #ea580c;
}

.rehab-person__guidance-stats-value .unit {
  font-size: 10px;
  color: #9a3412;
  font-weight: 500;
}

/* 学习统计区域 */
.rehab-person__study-section {
  margin-bottom: 20px;
}

.rehab-person__section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 4px;
}

.rehab-person__section-title span {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.rehab-person__section-line {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #07c160, transparent);
  border-radius: 1px;
  margin-left: 12px;
}

.rehab-person__study-stats {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.rehab-person__study-stats-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  background: #fff;
  border-radius: 16px;
  padding: 16px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-align: center;
}

.rehab-person__study-stats-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  border-radius: 0 0 2px 2px;
}

/*
.rehab-person__study-stats-item.today::before {
  background: linear-gradient(90deg, #ff6b35, #ff8f3f);
}

.rehab-person__study-stats-item.week::before {
  background: linear-gradient(90deg, #07c160, #36d174);
}

.rehab-person__study-stats-item.total::before {
  background: linear-gradient(90deg, #6366f1, #8b5cf6);
} */

.rehab-person__study-stats-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08), 0 3px 8px rgba(0, 0, 0, 0.03);
}

.rehab-person__study-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  margin-bottom: 12px;
}

.rehab-person__study-stats-item.today .rehab-person__study-icon {
  background: linear-gradient(135deg, rgba(255, 107, 53, 0.1), rgba(255, 143, 63, 0.15));
}

.rehab-person__study-stats-item.week .rehab-person__study-icon {
  background: linear-gradient(135deg, rgba(7, 193, 96, 0.1), rgba(54, 209, 116, 0.15));
}

.rehab-person__study-stats-item.total .rehab-person__study-icon {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.15));
}

.rehab-person__study-stats-item.courses .rehab-person__study-icon {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(251, 191, 36, 0.15));
}

.rehab-person__study-content {
  flex: 1;
  width: 100%;
}

.rehab-person__study-stats-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
  font-weight: 500;
  line-height: 1;
}

.rehab-person__study-stats-value {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 2px;
}

.rehab-person__study-stats-value .number {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1;
}

.rehab-person__study-stats-value .unit {
  font-size: 10px;
  color: #64748b;
  font-weight: 500;
}

.loading-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
}

/* 数字翻页动画 */
.rehab-person__flip {
  display: inline-block;
  perspective: 600px;
}

.flip-enter-active, .flip-leave-active {
  transition: transform 400ms cubic-bezier(0.4, 0, 0.2, 1), opacity 400ms ease;
  display: inline-block;
}

.flip-enter, .flip-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

/* 设置弹窗样式 */
.settings-popup {
  background: #fff;
  min-height: 200px;
}

.settings-popup__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #f1f5f9;
}

.settings-popup__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.settings-popup__close {
  cursor: pointer;
  padding: 4px;
  transition: all 0.3s ease;
}

.settings-popup__close:hover {
  color: #64748b;
  transform: scale(1.1);
}

.settings-popup__content {
  padding: 0;
}

.settings-popup__item {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  border-bottom: 1px solid #f8fafc;
}

.settings-popup__item:hover {
  background-color: #f8fafc;
}

.settings-popup__item:last-child {
  border-bottom: none;
}

.settings-popup__item-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 71, 87, 0.1);
  border-radius: 12px;
  margin-right: 16px;
}

.settings-popup__item-text {
  flex: 1;
}

.settings-popup__item-title {
  font-size: 16px;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 2px;
}

.settings-popup__item-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
}

/* 动画效果 */
@keyframes float {
  0% {
    transform: translateX(0) translateY(0);
  }
  100% {
    transform: translateX(-60px) translateY(-60px);
  }
}

@keyframes shine {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.2);
  }
}

/* 响应式设计 */
@media (max-width: 360px) {
  .rehab-person {
    padding: 0 12px 20px;
  }

  .rehab-person__card-content {
    padding: 16px;
  }

  .rehab-person__avatar {
    width: 52px;
    height: 52px;
  }

  .rehab-person__greeting {
    font-size: 16px;
  }

  .rehab-person__settings {
    width: 36px;
    height: 36px;
  }

  .rehab-person__study-stats {
    gap: 6px;
  }

  .rehab-person__study-stats-item {
    padding: 12px 8px;
  }

  .rehab-person__study-icon {
    width: 32px;
    height: 32px;
    margin-bottom: 8px;
  }

  .rehab-person__study-stats-label {
    font-size: 11px;
    margin-bottom: 4px;
  }

  .rehab-person__study-stats-value .number {
    font-size: 16px;
  }

  .rehab-person__study-stats-value .unit {
    font-size: 9px;
  }

  /* VIP会员与康复指导权益融合卡片响应式 */
  .rehab-person__vip-guidance-content {
    padding: 16px;
  }

  .rehab-person__vip-icon {
    width: 40px;
    height: 40px;
  }

  .rehab-person__vip-title {
    font-size: 15px;
  }

  .rehab-person__vip-desc {
    font-size: 12px;
  }

  .rehab-person__guidance-title {
    font-size: 14px;
  }

  .rehab-person__guidance-desc {
    font-size: 11px;
    margin-left: 12px;
  }

  .rehab-person__guidance-stats {
    gap: 8px;
  }

  .rehab-person__guidance-stats-item {
    padding: 10px 6px;
  }

  .rehab-person__guidance-stats-label {
    font-size: 10px;
    margin-bottom: 4px;
  }

  .rehab-person__guidance-stats-value .number {
    font-size: 16px;
  }

  .rehab-person__guidance-stats-value .unit {
    font-size: 9px;
  }

}

/* 暗色模式适配 */
@media (prefers-color-scheme: dark) {
  .rehab-person-page {
    background: linear-gradient(180deg, #1e293b 0%, #334155 100%);
  }

  .rehab-person__card-bg {
    background: linear-gradient(135deg, #334155 0%, #475569 100%);
  }

  .rehab-person__greeting {
    color: #f1f5f9;
  }

  .rehab-person__name {
    color: #cbd5e1;
  }

  .settings-popup {
    background: #334155;
  }

  .settings-popup__title {
    color: #f1f5f9;
  }

  .settings-popup__header {
    border-bottom-color: #475569;
  }

  .settings-popup__item {
    border-bottom-color: #475569;
  }

  .settings-popup__item:hover {
    background-color: #475569;
  }

  .settings-popup__item-title {
    color: #f1f5f9;
  }

  .settings-popup__item-desc {
    color: #cbd5e1;
  }

  .rehab-person__study-stats-item {
    background: #334155;
  }

  .rehab-person__study-stats-label {
    color: #cbd5e1;
  }

  .rehab-person__study-stats-value .number {
    color: #f1f5f9;
  }

  .rehab-person__section-title span {
    color: #f1f5f9;
  }

  /* VIP会员与康复指导权益融合卡片暗色模式 */
  .rehab-person__vip-guidance-bg {
    background: linear-gradient(135deg, #334155 0%, #475569 50%, #64748b 100%);
  }

  .rehab-person__vip-title {
    color: #f97316;
  }

  .rehab-person__vip-desc {
    color: #cbd5e1;
  }

  .rehab-person__guidance-title {
    color: #f97316;
  }

  .rehab-person__guidance-desc {
    color: #cbd5e1;
  }

  .rehab-person__guidance-stats-item {
    background: rgba(51, 65, 85, 0.8);
  }

  .rehab-person__guidance-stats-label {
    color: #cbd5e1;
  }

  .rehab-person__guidance-stats-value .number {
    color: #f97316;
  }

  .rehab-person__guidance-stats-value .unit {
    color: #cbd5e1;
  }

  .rehab-person__dashed-divider {
    background: repeating-linear-gradient(
      to right,
      rgba(203, 213, 225, 0.4) 0px,
      rgba(203, 213, 225, 0.4) 8px,
      transparent 8px,
      transparent 16px
    );
  }

}
</style>
